'use client'

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { REHYDRATE } from 'redux-persist'

import { ProductAttributeKey } from '@/lib/types/core/product-attribute-keys'
import type { IProductVariant } from '@/lib/types/core/product-variant'
import { resolveUnitOfMeasure } from '@/lib/types/core/unit-of-measure'
import type { CartLineItem, PurchaseMode, UpdateCartItemPayload as LegacyUpdateCartItemPayload } from '@/lib/types/operations/cart'
import type { ProductInventoryItem, ProductItem } from '@/lib/types/operations/product'
import type { AttributeValue } from '@/lib/utils/variant-attributes'
import { calculateLineTotal, cartLineIdentityKey, normalizePriceBreaks, ORDER_TAX_RATE, resolvePrice, resolveTieredPerYard, type PriceTier } from '@/lib/utils/price-resolution'
import { UnitOfMeasure } from '@prisma/client'

export type CartStep = 'shipping' | 'review'

export interface CartPersistedState {
  ownerId: string | null
  items: CartLineItem[]
  shipping: {
    address: Record<string, string | undefined> | null
    method: string | null
    cost: number
    // Checkout-only flags/derived data (Task 11). Additive to the shipping
    // object so setCartShipping/selectCartShipping keep their existing
    // signatures; these fields just widen what the object carries.
    // (createAccount was removed with guest checkout; a stale persisted value
    // rehydrates harmlessly since nothing reads it.)
    saveAddress?: boolean
    estimatedDays?: number
  }
}

export interface CartCurrentState {
  activeStep: CartStep | null
  isSubmitting: boolean
  drawerOpen: boolean
  loading: boolean
  error: string | null
}

export interface CartState {
  current: CartCurrentState
  persisted: CartPersistedState
}

export const cartPersistedDefaults: CartPersistedState = {
  ownerId: null,
  items: [],
  shipping: { address: null, method: null, cost: 0 }
}

const initialCurrent: CartCurrentState = {
  activeStep: null,
  isSubmitting: false,
  drawerOpen: false,
  loading: false,
  error: null
}

const initialState: CartState = {
  current: { ...initialCurrent },
  persisted: cartPersistedDefaults
}

// ─── Ported from the pre-redux-persist cart slice (src/lib/store/shared/cart.ts) ──
//
// The PDP / cart-drawer / cart-page consumers dispatch a rich `addToCart`
// payload (productId + quantity + purchaseMode/pieces/lengthPerPiece + a raw
// variantId) rather than a fully-built CartLineItem, and rely on the reducer
// to: resolve/construct the IProductVariant, validate against inventory, find
// an existing line by productId+variantId (not by cart-item id) to merge
// quantities, and price the line per purchase mode (fixed / custom-cut /
// full-roll). That logic is preserved verbatim below so no consumer regresses;
// only the cookie-sync side effect (removed — redux-persist owns persistence
// now) and the CART_STATE_VERSION cookie-hydration guard (superseded by
// redux-persist's own per-key `migrate`, see store.ts) were dropped.

const CustomProductAttribute = {
  ...ProductAttributeKey,
  MIN_QUANTITY: 'MIN_QUANTITY',
  MAX_QUANTITY: 'MAX_QUANTITY'
} as const

type ExtendedProductAttribute = (typeof CustomProductAttribute)[keyof typeof CustomProductAttribute]

interface AddToCartPayload {
  productId: string
  quantity: number
  product: ProductItem
  variantId?: string
  unitOfMeasure?: UnitOfMeasure
  purchaseMode?: PurchaseMode
  pieces?: number
  lengthPerPiece?: number
  note?: string
}

const getAttributeValue = (
  variant: IProductVariant,
  attribute: ExtendedProductAttribute,
  defaultValue?: number
): number => {
  if (!variant.attributeValues) return defaultValue ?? 0
  const value = (variant.attributeValues as Record<string, AttributeValue>)[attribute]
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const parsed = parseFloat(value)
    return isNaN(parsed) ? (defaultValue ?? 0) : parsed
  }
  if (typeof value === 'boolean') return value ? 1 : 0
  return defaultValue ?? 0
}

export const validateInventoryQuantity = (
  quantity: number,
  inventory: ProductInventoryItem | null | undefined,
  variant?: IProductVariant | null,
  product?: ProductItem
): { valid: boolean; message?: string } => {
  if (!inventory) {
    if (product && typeof product.price === 'number' && product.price > 0) return { valid: true }
    return { valid: false, message: 'Product is out of stock' }
  }

  if (quantity === undefined || quantity === null || quantity <= 0) {
    return { valid: false, message: 'Invalid quantity' }
  }

  const availableQuantity = inventory.quantity || 0

  if (availableQuantity === 0) {
    if (product && typeof product.price === 'number' && product.price > 0) return { valid: true }
    return { valid: false, message: 'Product is out of stock' }
  }

  if (quantity > availableQuantity) {
    return {
      valid: false,
      message: `Only ${availableQuantity} ${inventory.unitOfMeasure.toLowerCase()} available`
    }
  }

  if (variant) {
    const minQuantity = getAttributeValue(variant, CustomProductAttribute.MIN_QUANTITY, 1)
    const maxQuantity = getAttributeValue(variant, CustomProductAttribute.MAX_QUANTITY, availableQuantity)

    if (quantity < minQuantity) {
      return { valid: false, message: `Minimum quantity is ${minQuantity} ${inventory.unitOfMeasure.toLowerCase()}` }
    }
    if (quantity > maxQuantity) {
      return { valid: false, message: `Maximum quantity is ${maxQuantity} ${inventory.unitOfMeasure.toLowerCase()}` }
    }

    // Only enforce when a numeric incrementStep is actually present and
    // meaningful (> 0) — legacy carts and whole-unit (EACH) products have no
    // incrementStep, and must stay untouched by this check.
    const step = variant.incrementStep
    if (typeof step === 'number' && step > 0) {
      const nearestStepMultiple = Math.round(quantity / step) * step
      if (Math.abs(nearestStepMultiple - quantity) > 1e-6) {
        return { valid: false, message: `Quantity must be in increments of ${step}` }
      }
    }
  }

  return { valid: true }
}

const createInventoryItem = (baseInventory: {
  sku: string
  productId?: string | null
  variantId?: string | null
  quantity?: number | null
  unitOfMeasure: UnitOfMeasure
  shopLocationId: string
  location?: { id: string; name: string; address: string } | null
  variant?: {
    id: string
    sku: string
    name: string
    color: string
    widthInches: number
    heightInches: number
    pricePerUnit: number
  } | null
}): ProductInventoryItem => ({
  sku: baseInventory.sku || '',
  productId: baseInventory.productId || '',
  quantity: baseInventory.quantity ?? null,
  reorderPoint: 0,
  reorderQuantity: 0,
  unitOfMeasure: baseInventory.unitOfMeasure ?? UnitOfMeasure.YARD,
  shopLocationId: baseInventory.shopLocationId || '',
  variantId: baseInventory.variantId ?? null,
  id: `${baseInventory.sku}-${baseInventory.shopLocationId}`,
  location: baseInventory.location ?? undefined,
  variant: baseInventory.variant ?? undefined,
  isAggregated: false,
  locationCount: 1
})

/** Tier-aware per-unit price for a variant, using the real retailPrice/
 * wholesalePrice columns (via resolvePrice) ahead of the legacy
 * attributeValues/pricePerUnit fallback. Only variants carry a wholesale
 * price (no column exists at the product level), so a variant with neither
 * column populated falls through to the pre-existing fallback untouched. */
const resolveTierUnitPrice = (variant: IProductVariant | undefined, tier: PriceTier): number | null => {
  if (!variant) return null
  return resolvePrice(
    {
      retailPrice: variant.retailPrice ?? null,
      wholesalePrice: variant.wholesalePrice ?? null,
      rollPrice: variant.rollPrice ?? null,
      rollLengthUnits: variant.rollLengthUnits ?? null
    },
    tier
  )
}

/** Resolves the per-unit (or per-roll) price used to price a line, mirroring
 * the fallback chain the standalone cart-item display components already use
 * (tier column -> variant attributeValues -> variant field -> product field).
 * Stored on the item as `calculatedPrice` so summary surfaces (cart page/
 * drawer totals, checkout) don't need product/variant context to reprice at
 * add time; `getCartItemLineTotal` re-derives this at DISPLAY time whenever a
 * variant is present, so a wholesale sign-in after an anonymous add still
 * shows the right number without a rehydrate migration. */
export const resolveCartItemUnitPrice = (
  item: Pick<CartLineItem, 'variant' | 'product' | 'purchaseMode'> &
    Partial<Pick<CartLineItem, 'lengthPerPiece' | 'pieces'>>,
  tier: PriceTier = 'RETAIL'
): number => {
  const variant = item.variant

  if (item.purchaseMode === 'full-roll') {
    // No wholesale roll price exists on ProductVariant (single rollPrice
    // column) — roll pricing is tier-agnostic, matching resolveLineUnitPrice's
    // server-side full-roll branch.
    const rollPrice =
      variant?.rollPrice ?? (variant?.attributeValues?.[ProductAttributeKey.ROLL_PRICE] as number | undefined) ?? item.product.rollPrice
    if (typeof rollPrice === 'number' && rollPrice > 0) return rollPrice
  }

  if (item.purchaseMode === 'custom-cut') {
    const tierPrice = resolveTierUnitPrice(variant, tier)
    const pricePerUnit =
      (tierPrice && tierPrice > 0 ? tierPrice : null) ??
      variant?.pricePerUnit ??
      (variant?.attributeValues?.[ProductAttributeKey.PRICE_PER] as number | undefined) ??
      item.product.pricePerUnit
    if (typeof pricePerUnit === 'number' && pricePerUnit > 0) {
      // Volume-tier pricing on the line's TOTAL yards (retail only), the same
      // rule resolveLineUnitPrice applies server-side at reserve time.
      const lineYards = (item.lengthPerPiece ?? 0) * (item.pieces ?? 1)
      if (tier === 'RETAIL' && lineYards > 0) {
        return resolveTieredPerYard(normalizePriceBreaks(variant?.priceBreaks), lineYards, pricePerUnit)
      }
      return pricePerUnit
    }
  }

  const tierFallback = resolveTierUnitPrice(variant, tier)
  const fallback =
    (tierFallback && tierFallback > 0 ? tierFallback : null) ??
    variant?.pricePerUnit ??
    (variant?.attributeValues?.[ProductAttributeKey.PRICE_PER] as number | undefined) ??
    item.product.price ??
    0
  return fallback || 0
}

/** Purchase-mode-aware line total. `calculatedPrice` (falling back to
 * product.price) is treated as a per-unit / per-roll price, not a
 * pre-multiplied total — custom-cut multiplies by lengthPerPiece*pieces and
 * full-roll multiplies by quantity, matching calculateLineTotal's contract.
 * This is the "richer semantics" callers (selectCartTotal, selectCartStats)
 * need instead of a naive `price * quantity`.
 *
 * A variant-priced line is re-derived from `item.variant` for the CURRENT
 * `tier` rather than trusting the stored `calculatedPrice` (which was only
 * ever a snapshot of the tier known at add time) — this is what makes a cart
 * added while anonymous show wholesale numbers once a wholesale customer
 * signs in, with no rehydrate migration needed. A variant-less line has no
 * wholesale price to re-derive (only ProductVariant carries the column), so
 * it keeps using the stored `calculatedPrice` untouched. */
export const getCartItemLineTotal = (item: CartLineItem, tier: PriceTier = 'RETAIL'): number => {
  const unitPrice = item.variant
    ? resolveCartItemUnitPrice(item, tier)
    : (item.calculatedPrice ?? item.product.price ?? 0)

  if (item.purchaseMode === 'custom-cut') {
    return calculateLineTotal({
      purchaseMode: 'custom-cut',
      lengthPerPiece: item.lengthPerPiece ?? 0,
      pieces: item.pieces,
      pricePerUnit: unitPrice
    })
  }

  if (item.purchaseMode === 'full-roll') {
    return calculateLineTotal({ purchaseMode: 'full-roll', quantity: item.quantity, rollPrice: unitPrice })
  }

  return calculateLineTotal({ purchaseMode: 'fixed', quantity: item.quantity, unitPrice })
}

const buildVariant = (
  product: ProductItem,
  variantId: string | undefined,
  unitOfMeasure: UnitOfMeasure,
  now: string
): IProductVariant | null => {
  if (!variantId || !product.variants) return null
  const foundVariant = product.variants.find((v) => v.id === variantId)
  if (!foundVariant || !product.id) return null

  return {
    id: foundVariant.id,
    productId: product.id,
    sku: foundVariant.sku ?? '',
    name: foundVariant.name ?? '',
    attributes: [ProductAttributeKey.COLOR, ProductAttributeKey.WIDTH, ProductAttributeKey.HEIGHT],
    attributeValues: {
      [ProductAttributeKey.COLOR]: foundVariant.color ?? null,
      [ProductAttributeKey.WIDTH]: foundVariant.widthInches ?? null,
      [ProductAttributeKey.HEIGHT]: foundVariant.heightInches ?? null,
      [ProductAttributeKey.PRICE_PER]: foundVariant.pricePerUnit ?? null,
      [ProductAttributeKey.ROLL_LENGTH]: foundVariant.rollLengthUnits ?? null,
      [ProductAttributeKey.ROLL_PRICE]: foundVariant.rollPrice ?? null,
      [ProductAttributeKey.LENGTH]: null,
      [ProductAttributeKey.PRICE_BREAKS]: null
    },
    // Real tier columns (not attributeValues) — carried on the line item so
    // getCartItemLineTotal can re-derive the correct tier's price at display
    // time, however long the line has sat in a persisted cart.
    retailPrice: foundVariant.retailPrice ?? null,
    wholesalePrice: foundVariant.wholesalePrice ?? null,
    rollPrice: foundVariant.rollPrice ?? null,
    rollLengthUnits: foundVariant.rollLengthUnits ?? null,
    // Volume tiers: without this the cart displays the flat rate while the
    // server charges the tiered rate, so displayed and charged diverge.
    priceBreaks: (foundVariant as { priceBreaks?: unknown }).priceBreaks ?? null,
    // Carried so validateInventoryQuantity's increment check can fire on
    // cart writes; undefined when the list-view payload doesn't supply it
    // (the check skips in that case).
    incrementStep: foundVariant.incrementStep ?? undefined,
    // Swatch fill + photo for the cart line thumbnail (wrap-vinyl lines have
    // no product media; the swatchHex fallback is the only visual identity)
    swatchHex: (foundVariant as { swatchHex?: string | null }).swatchHex ?? null,
    media: (foundVariant as { media?: IProductVariant['media'] }).media ?? null,
    createdAt: now,
    updatedAt: now,
    unitOfMeasure
  } as IProductVariant
}

/** The line's identity key from its own stored fields. Uses the BUILT
 *  variant's id (not the raw payload variantId) on both sides of every
 *  comparison, so a variantId that failed to resolve cannot split into
 *  permanently duplicate lines. */
const lineIdentityKey = (
  item: Pick<CartLineItem, 'productId' | 'variant' | 'purchaseMode' | 'lengthPerPiece'>
): string =>
  cartLineIdentityKey({
    productId: item.productId,
    variantId: item.variant?.id ?? null,
    purchaseMode: item.purchaseMode,
    lengthPerPiece: item.lengthPerPiece ?? null
  })

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    // ─── Canonical actions (Task 10/11 checkout wizard contract) ───────────
    addItem(state, action: PayloadAction<CartLineItem>) {
      const existing = state.persisted.items.find((i) => i.id === action.payload.id)
      if (existing) {
        existing.quantity += action.payload.quantity
      } else {
        state.persisted.items.push(action.payload)
      }
    },
    updateItem(state, action: PayloadAction<{ cartItemId: string; patch: Partial<CartLineItem> }>) {
      const idx = state.persisted.items.findIndex((i) => i.id === action.payload.cartItemId)
      if (idx >= 0) {
        state.persisted.items[idx] = { ...state.persisted.items[idx], ...action.payload.patch }
      }
    },
    removeItem(state, action: PayloadAction<string>) {
      state.persisted.items = state.persisted.items.filter((i) => i.id !== action.payload)
    },
    clearCart(state) {
      state.persisted = cartPersistedDefaults
      state.current = { ...initialCurrent }
    },
    claimCartOwner(state, action: PayloadAction<string>) {
      state.persisted.ownerId = action.payload
    },
    setCartShipping(state, action: PayloadAction<Partial<CartPersistedState['shipping']>>) {
      state.persisted.shipping = { ...state.persisted.shipping, ...action.payload }
    },
    setCartActiveStep(state, action: PayloadAction<CartStep | null>) {
      state.current.activeStep = action.payload
    },
    setCartSubmitting(state, action: PayloadAction<boolean>) {
      state.current.isSubmitting = action.payload
    },
    setCartDrawerOpen(state, action: PayloadAction<boolean>) {
      state.current.drawerOpen = action.payload
    },

    // ─── Rich legacy-shaped actions kept for PDP / cart-drawer consumers ──
    openCartDrawer(state) {
      state.current.drawerOpen = true
    },
    closeCartDrawer(state) {
      state.current.drawerOpen = false
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.current.loading = action.payload
    },
    setError(state, action: PayloadAction<string | null>) {
      state.current.error = action.payload
    },
    addToCart(state, action: PayloadAction<AddToCartPayload>) {
      const { productId, quantity, variantId, product } = action.payload
      const purchaseMode: PurchaseMode = action.payload.purchaseMode ?? 'fixed'
      const pieces = action.payload.pieces ?? 1
      const lengthPerPiece = action.payload.lengthPerPiece ?? null
      const note = action.payload.note ?? null
      const now = new Date().toISOString()

      const unitOfMeasure =
        action.payload.unitOfMeasure ||
        resolveUnitOfMeasure(product.variants?.find((v) => v.id === variantId)) ||
        UnitOfMeasure.METER

      const variant = buildVariant(product, variantId, unitOfMeasure, now)

      const baseInventory = variant?.inventory?.[0] ?? product.inventoryItems?.[0]
      const inventory: ProductInventoryItem | null = baseInventory
        ? createInventoryItem({ ...baseInventory, unitOfMeasure })
        : null

      const validation = validateInventoryQuantity(quantity, inventory, variant, product)
      if (!validation.valid) {
        state.current.error = validation.message || 'Invalid quantity'
        return
      }

      const incomingKey = lineIdentityKey({
        productId,
        variant: variant ?? undefined,
        purchaseMode,
        lengthPerPiece
      })
      const existingItem = state.persisted.items.find((item) => lineIdentityKey(item) === incomingKey)

      if (existingItem) {
        const newQuantity = existingItem.quantity + quantity
        const totalValidation = validateInventoryQuantity(newQuantity, inventory, variant, product)
        if (!totalValidation.valid) {
          state.current.error = totalValidation.message || 'Invalid quantity'
          return
        }

        // Identical selection (equal identity key): merge by incrementing.
        // purchaseMode, lengthPerPiece, and unitOfMeasure are equal by key
        // construction and are never overwritten here; a differing selection
        // never reaches this branch (it forks a new line below).
        existingItem.quantity = newQuantity
        if (purchaseMode === 'custom-cut') existingItem.pieces += pieces
        existingItem.updatedAt = now
        if (note !== null) existingItem.note = note
        existingItem.calculatedPrice = resolveCartItemUnitPrice(existingItem)
      } else {
        const newItem: CartLineItem = {
          id: crypto.randomUUID(),
          productId,
          quantity,
          // AddToCartPayload carries the list-view ProductItem shape (its
          // `variants` field is a narrower inline type than CartLineItem's
          // full IProductVariant[]); the cart only ever reads name/price/media
          // off it, so this is a safe widening, not a data-shape lie.
          product: product as CartLineItem['product'],
          variant: variant ?? undefined,
          createdAt: now,
          updatedAt: now,
          addedAt: now,
          unitOfMeasure,
          purchaseMode,
          pieces,
          lengthPerPiece,
          note
        }
        newItem.calculatedPrice = resolveCartItemUnitPrice(newItem)
        state.persisted.items.push(newItem)
      }

      state.current.error = null
    },
    updateCartItem(state, action: PayloadAction<LegacyUpdateCartItemPayload & { cartItemId: string }>) {
      const { cartItemId, quantity } = action.payload
      const item = state.persisted.items.find((i) => i.id === cartItemId)
      if (!item) {
        state.current.error = 'Cart item not found'
        return
      }

      const baseInventory = item.variant?.inventory?.[0] ?? item.product.inventoryItems?.[0]
      const inventory: ProductInventoryItem | null = baseInventory ? createInventoryItem(baseInventory) : null

      const validation = validateInventoryQuantity(quantity, inventory, item.variant, item.product)
      if (!validation.valid) {
        state.current.error = validation.message || 'Invalid quantity'
        return
      }

      item.quantity = quantity
      item.updatedAt = new Date().toISOString()

      if (action.payload.purchaseMode !== undefined) item.purchaseMode = action.payload.purchaseMode
      if (action.payload.pieces !== undefined) item.pieces = action.payload.pieces
      if (action.payload.lengthPerPiece !== undefined) item.lengthPerPiece = action.payload.lengthPerPiece
      if (action.payload.note !== undefined) item.note = action.payload.note

      item.calculatedPrice = resolveCartItemUnitPrice(item)

      // An edit that changed purchaseMode or cut length changed the line's
      // IDENTITY. Re-merge into a sibling with the same key instead of
      // leaving two lines that represent one selection (remove-then-add).
      const editedKey = lineIdentityKey(item)
      const sibling = state.persisted.items.find(
        (other) => other.id !== item.id && lineIdentityKey(other) === editedKey
      )
      if (sibling) {
        sibling.quantity += item.quantity
        if (sibling.purchaseMode === 'custom-cut') sibling.pieces += item.pieces
        sibling.updatedAt = item.updatedAt
        sibling.calculatedPrice = resolveCartItemUnitPrice(sibling)
        state.persisted.items = state.persisted.items.filter((other) => other.id !== item.id)
      }

      state.current.error = null
    },
    removeFromCart(state, action: PayloadAction<{ cartItemId: string }>) {
      state.persisted.items = state.persisted.items.filter((item) => item.id !== action.payload.cartItemId)
      state.current.error = null
    },
    validateCart(state) {
      state.persisted.items = state.persisted.items.filter((item) => {
        const baseInventory = item.variant?.inventory?.[0] ?? item.product.inventoryItems?.[0]
        const inventory: ProductInventoryItem | null = baseInventory ? createInventoryItem(baseInventory) : null
        const validation = validateInventoryQuantity(item.quantity, inventory, item.variant, item.product)
        if (!validation.valid) {
          state.current.error = validation.message || 'Invalid quantity'
          return false
        }
        return true
      })
    }
  },
  extraReducers: (builder) => {
    // `drawerOpen` is transient UI state, but the root persist config
    // whitelists the whole `cart` slice, so a blob captured while the drawer
    // was open rehydrates it OPEN. The drawer's full-width `bg-black/50`
    // backdrop (z-1050, left-0 right-0) then lands over whatever page loaded,
    // including /checkout and /pay, where it silently intercepts clicks on the
    // terms checkbox and the Pay button. Never restore an open drawer.
    builder.addMatcher(
      (action) => action.type === REHYDRATE,
      (state) => {
        state.current.drawerOpen = false
      },
    )
  },
})

export const {
  addItem,
  updateItem,
  removeItem,
  clearCart,
  claimCartOwner,
  setCartShipping,
  setCartActiveStep,
  setCartSubmitting,
  setCartDrawerOpen,
  openCartDrawer,
  closeCartDrawer,
  setLoading,
  setError,
  addToCart,
  updateCartItem,
  removeFromCart,
  validateCart
} = cartSlice.actions

export const selectCartItems = (state: { cart: CartState }) => state.cart.persisted.items
export const selectCartOwnerId = (state: { cart: CartState }) => state.cart.persisted.ownerId
export const selectCartShipping = (state: { cart: CartState }) => state.cart.persisted.shipping
export const selectCartDrawerOpen = (state: { cart: CartState }) => state.cart.current.drawerOpen
export const selectCartLoading = (state: { cart: CartState }) => state.cart.current.loading
export const selectCartError = (state: { cart: CartState }) => state.cart.current.error
export const selectCartSubmitting = (state: { cart: CartState }) => state.cart.current.isSubmitting
export const selectCartActiveStep = (state: { cart: CartState }) => state.cart.current.activeStep

export const selectCartTotal = (state: { cart: CartState }, tier: PriceTier = 'RETAIL') =>
  state.cart.persisted.items.reduce((sum, item) => sum + getCartItemLineTotal(item, tier), 0)

export interface CartInventoryStatus {
  id: string
  productId: string
  quantity: number
  inStock: boolean
  availableQuantity: number
  maxOrderQuantity: number | null
  locationCount: number
}

export const selectCartInventoryStatus = (state: { cart: CartState }): CartInventoryStatus[] =>
  state.cart.persisted.items.map((item) => {
    const inventories = item.product.inventoryItems || []
    return {
      id: item.id,
      productId: item.product.id!,
      quantity: item.quantity,
      inStock: inventories.some((inv) => (inv.quantity || 0) > 0),
      availableQuantity: inventories.reduce((total, inv) => total + (inv.quantity || 0), 0),
      maxOrderQuantity: item.product.maxOrderQuantity || null,
      locationCount: inventories.filter((inv) => (inv.quantity || 0) > 0).length
    }
  })

export const selectCartStats = (state: { cart: CartState }, tier: PriceTier = 'RETAIL') => {
  const items = state.cart.persisted.items
  const subtotal = items.reduce((total, item) => total + getCartItemLineTotal(item, tier), 0)
  const tax = subtotal * ORDER_TAX_RATE
  return {
    subtotal,
    tax,
    total: subtotal + tax,
    isEmpty: !items.length,
    itemCount: items.length,
    hasError: !!state.cart.current.error,
    isLoading: state.cart.current.loading
  }
}

export default cartSlice.reducer
